import type { WorkerRunType } from "@validereinc/domain";
import { create } from "zustand";

export type RunWorkerDrawerQueuedRunType = {
  /** Worker run id returned from `POST /worker_runs`. */
  id: string;
  workerName: string;
  version: number;
  /** ISO datetime from the run payload when enqueued; older queue entries may omit this. */
  started_at?: string;
  /** Request inputs captured when the run was queued; superseded by polled run data when present. */
  inputs?: Record<string, unknown>;
  /**
   * Last worker run payload from the API (create or list poll). Persisted with the queue so
   * completed/failed runs are not refetched on full page load.
   */
  polledRun?: WorkerRunType;
  /**
   * Set when `/worker_runs/search` returned successfully for a batch that requested this id
   * but no row was present. Stops infinite polling and allows dismiss; status UI should not
   * treat the run as still running.
   */
  notReturnedBySearchPoll?: boolean;
};

export type RunWorkerDrawerStoreState = {
  /** Whether the Run Worker drawer is open. */
  isOpen: boolean;
  /** Open or close the Run Worker drawer. */
  setIsOpen: (isOpen: boolean) => void;
  /** Currently selected worker id, or undefined if none. */
  selectedWorkerId: string | undefined;
  /** Set the selected worker id. */
  setSelectedWorkerId: (id: string | undefined) => void;
  /** Currently selected worker version number, or undefined if none. */
  selectedVersion: number | undefined;
  /** Set the selected worker version. */
  setSelectedVersion: (version: number | undefined) => void;
  /** Persisted form input values so they survive page navigations. */
  formInputValues: Record<string, unknown>;
  /** Persist the current form input values to the store. */
  setFormInputValues: (values: Record<string, unknown>) => void;
  /** Clear worker and version selection and form input values. */
  resetSelection: () => void;
  /** Number of queued worker runs currently in STARTED status. */
  startedWorkerRunsCount: number;
  /** Update the global count of currently running worker runs. */
  setStartedWorkerRunsCount: (count: number) => void;
};

/**
 * Global store for the Run Worker drawer open/close state and selection.
 * Use from layout (to render the drawer) and from any page (e.g. "Run Worker" button) to open it.
 */
export const useRunWorkerDrawer = create<RunWorkerDrawerStoreState>()(
  (set) => ({
    isOpen: false,
    setIsOpen: (isOpen: boolean) => set({ isOpen }),
    selectedWorkerId: undefined,
    setSelectedWorkerId: (selectedWorkerId) => set({ selectedWorkerId }),
    selectedVersion: undefined,
    setSelectedVersion: (selectedVersion) => set({ selectedVersion }),
    formInputValues: {},
    setFormInputValues: (formInputValues) => set({ formInputValues }),
    resetSelection: () =>
      set({
        selectedWorkerId: undefined,
        selectedVersion: undefined,
        formInputValues: {},
      }),
    startedWorkerRunsCount: 0,
    setStartedWorkerRunsCount: (startedWorkerRunsCount) =>
      set({ startedWorkerRunsCount }),
  })
);
